import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, ButtonGroup, Card, Heading, Modal, Paragraph, Text } from '@zillow/constellation-10';

import HelmetContainer from '@common/components/HelmetContainer';
import SeoConstants from '@renter-hub/constants/seoConstants';
import faqs from '@renter-hub/constants/FaqConstants';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import NotificationContainer from '@common/components/NotificationContainer';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import userPersonas from '@common/constants/userPersonas';
import Calendar from '@common/components/Calendar';
import { RenterTransactionProps } from '@renter-hub/models/Types';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import { RequestInitiatePaymentInterface } from '@renter-hub/models/RequestsTypes';
import { formatDate, formatTimestampToYMDString } from '@common/paymentsUtils/dateUtils';
import { getVerifiedPaymentMethods } from '@common/paymentsUtils/multiplePaymentMethodsUtil';
import { RootState } from '../../../../store/types';
import AvailablePaymentMethods from '../../components/AvailablePaymentMethods';

interface Props {
  today: Date;
  transaction: RenterTransactionProps;
  failedPaymentEmail: boolean;
  onSubmit: (rentPaymentInfo: RequestInitiatePaymentInterface) => any;
  goToDashboard: () => void;
}

export const PayPastDueRentPaymentPage = (props: Props) => {
  const paymentMethods = useSelector((state: RootState) => state.payments.renter.dashboard.paymentMethods);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(() => {
    if (!paymentMethods?.length) return '';

    // Get verified payment methods using the shared utility
    const verifiedPaymentMethods = getVerifiedPaymentMethods(paymentMethods);

    // Use the first verified payment method
    return verifiedPaymentMethods[0]?.id || '';
  });

  const { today, transaction, failedPaymentEmail } = props;

  const goToDashboard = () => {
    props.goToDashboard();
  };

  const onSubmit = async () => {
    setIsLoading(true);

    const response = await props.onSubmit({
      scheduleMonth: transaction.scheduleMonth,
      paymentDate: formatTimestampToYMDString(new Date().getTime()),
      recurring: false,
      encodedPaymentMethodId: selectedPaymentMethodId,
    });

    const { error } = response;
    if (error) {
      setIsLoading(false);
    }
  };

  const renderCalendarTitle = () => {
    if (failedPaymentEmail) {
      return (
        <Text css={{ color: 'red.500' }} textStyle="body-sm">
          We couldn&apos;t process your payment using your payment method. Contact your bank, or try a different payment
          method.
        </Text>
      );
    } else {
      return <Text css={{ color: 'red.600' }}>Your rent payment is overdue</Text>;
    }
  };

  const renderRentPaymentSection = () => {
    const showCardFee = transaction.zgFee !== 0;

    return (
      <Box css={{ marginTop: 'token(spacing.md) !important', marginBottom: 'token(spacing.md) !important' }}>
        <Card.Root elevated={false}>
          <Card.Header css={{ padding: 'token(spacing.sm) !important' }}>
            <Paragraph textStyle="body-bold">Rent payment</Paragraph>
            <Text className="fs-exclude">Due on {formatDate(transaction.dueDate, 'MMMM d, yyyy')}</Text>
          </Card.Header>
          <Card.Body
            css={{
              paddingBottom: 'token(spacing.sm) !important',
              paddingRight: 'token(spacing.sm) !important',
              paddingLeft: 'token(spacing.sm) !important',
            }}
          >
            <Box
              css={{
                display: 'grid',
                gridTemplateColumns: '9fr 3fr',
                gap: 'token(spacing.xs)',
              }}
            >
              <Box css={{ paddingBottom: 'token(spacing.xs) !important' }}>
                <Text>Rent amount</Text>
              </Box>
              <Box css={{ padding: 'token(spacing.xs) !important', textAlign: 'right' }}>
                <Text>{centsToDollars(transaction.amount)}</Text>
              </Box>
              {showCardFee && (
                <>
                  <Box
                    css={{ paddingTop: 'token(spacing.xs) !important', paddingBottom: 'token(spacing.xs) !important' }}
                  >
                    <Text>Card fee</Text>
                  </Box>
                  <Box css={{ padding: 'token(spacing.xs) !important', textAlign: 'right' }}>
                    <Text>{centsToDollars(transaction.zgFee)}</Text>
                  </Box>
                </>
              )}
              <Box css={{ paddingTop: 'token(spacing.xs) !important', paddingBottom: 'token(spacing.xs) !important' }}>
                <Text textStyle="body-bold">Total payment</Text>
              </Box>
              <Box css={{ padding: 'token(spacing.xs) !important', textAlign: 'right' }}>
                <Text textStyle="body-bold">{centsToDollars(transaction.totalAmount)}</Text>
              </Box>
            </Box>
          </Card.Body>
        </Card.Root>
      </Box>
    );
  };

  return (
    <Modal
      size="sm"
      open={true}
      trigger={null}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          goToDashboard();
        }
      }}
      header={
        <Box css={{ padding: '16px 48px 16px 16px !important' }}>
          <Heading level={2} textStyle="heading-xs">
            Your {formatDate(transaction.dueDate, 'MMMM d')} rent is overdue
          </Heading>
        </Box>
      }
      body={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          <HelmetContainer {...SeoConstants.payPastDueRentPaymentPage} />
          <NotificationContainer persona={userPersonas.RENTER} />

          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'token(spacing.sm)',
              marginTop: 'token(spacing.md) !important',
            }}
          >
            <Calendar title={'Pay on'} dateLine1={formatDate(today, 'MMM do')} />
            {renderCalendarTitle()}
          </Box>
          {renderRentPaymentSection()}
          <AvailablePaymentMethods
            renderSelect
            onOptionChange={(e) => setSelectedPaymentMethodId(e.target.value)}
            isLoading={isLoading}
            selectedPaymentMethodId={selectedPaymentMethodId}
          />
          <Text textStyle="body-sm">
            Your payment will be processed immediately. If this date falls on a weekend or holiday, we&apos;ll send the
            payment&nbsp;
          </Text>
          <Text textStyle="body-sm-bold">1 business day later.</Text>
          <Box css={{ marginTop: 'token(spacing.md) !important' }}>
            <FaqComponent faqs={faqs.PAYMENT_SETTINGS_V2.OVERDUE_SCHEDULING_PAGE.body()} />
          </Box>
        </Box>
      }
      footer={
        <ButtonGroup
          aria-label="action-buttons"
          role="group"
          css={{
            padding: 'token(spacing.sm) !important',
          }}
        >
          <DebouncedButton emphasis="filled" onClick={onSubmit} loading={isLoading}>
            {failedPaymentEmail ? 'Try again' : 'Send rent payment now'}
          </DebouncedButton>
        </ButtonGroup>
      }
    />
  );
};

export default PayPastDueRentPaymentPage;
